/**
 * Turning whatever was thrown into a report worth reading.
 *
 * Pure, and in `packages/core` for the reason everything else here is: the web
 * app, the native app, the desktop shell and the API all need identical
 * normalisation, and four implementations would drift into four vocabularies
 * for the same crash.
 */
import { scrub } from './redact.js';
import type {
  Breadcrumb, ErrorReport, NormalizedError, ReportContext, ReportKind,
} from './types.js';

/**
 * JavaScript lets you throw anything.
 *
 * `throw 'nope'`, `throw {code: 500}`, `throw null` and a rejected promise
 * carrying `undefined` are all reachable, and a reporter that assumes `Error`
 * turns a real crash into `undefined is not an object` inside the reporter —
 * losing the original and reporting itself instead.
 */
export function normalizeError(thrown: unknown): NormalizedError {
  if (thrown instanceof Error) {
    return {
      name: thrown.name || 'Error',
      message: scrub(thrown.message || ''),
      stack: thrown.stack ? scrub(thrown.stack) : null,
    };
  }

  // A DOMException, a React Native native-module rejection, or anything else
  // shaped like an error without inheriting from it.
  if (typeof thrown === 'object' && thrown !== null) {
    const o = thrown as { name?: unknown; message?: unknown; stack?: unknown };
    const message = typeof o.message === 'string' ? o.message : safeStringify(thrown);
    return {
      name: typeof o.name === 'string' && o.name ? o.name : 'Error',
      message: scrub(message),
      stack: typeof o.stack === 'string' ? scrub(o.stack) : null,
    };
  }

  return {
    name: 'NonError',
    message: scrub(thrown === undefined ? 'undefined' : String(thrown)),
    stack: null,
  };
}

/**
 * `JSON.stringify` throws on a circular reference, which a thrown object very
 * often has — a fetch error carrying its request, a React error carrying a
 * fiber. Failing here would lose the report.
 */
function safeStringify(value: unknown): string {
  const seen = new WeakSet<object>();
  try {
    return JSON.stringify(value, (_k, v: unknown) => {
      if (typeof v === 'object' && v !== null) {
        if (seen.has(v)) return '[circular]';
        seen.add(v);
      }
      return v;
    }) ?? String(value);
  } catch {
    return Object.prototype.toString.call(value);
  }
}

/**
 * Volatile parts of a message, removed before grouping.
 *
 * `Request abc-123 failed after 4821ms` and `Request def-456 failed after
 * 51ms` are one bug. Grouped on the raw message they are two, and by the
 * morning they are two hundred — which is how a crash list stops being read.
 */
const VOLATILE: readonly (readonly [RegExp, string])[] = [
  // UUIDs, then any long hex — ids, hashes, and the placeholder redaction
  // leaves behind, so a redacted and an unredacted copy of one bug still group.
  [/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '<id>'],
  [/\[redacted\]/g, '<id>'],
  [/\b[0-9a-f]{8,}\b/gi, '<id>'],
  // ISO timestamps, then bare numbers. Durations, counts, byte offsets and
  // line numbers in a minified bundle are all noise for grouping.
  [/\d{4}-\d{2}-\d{2}T[\d:.]+Z?/g, '<time>'],
  // No trailing \b on purpose. `4821ms` has no word boundary between the `1`
  // and the `m`, so a trailing boundary left every duration, pixel value and
  // byte count in place — and durations are the single most common volatile
  // part of an error message. The first version of this had it and grouped
  // `failed after 4821ms` and `failed after 51ms` as two separate bugs.
  [/\b\d+(?:\.\d+)?/g, '<n>'],
  // Quoted values — a symbol, a filename, a user's tag. The shape of the
  // message is the bug; the value that walked into it is not.
  [/'[^']*'/g, "'<v>'"],
  [/"[^"]*"/g, '"<v>"'],
];

function stripVolatile(text: string): string {
  let out = text;
  for (const [pattern, replacement] of VOLATILE) out = out.replace(pattern, replacement);
  return out;
}

/**
 * The first frame of the stack that belongs to us.
 *
 * Framework frames are shared by every unrelated bug, so grouping on the
 * topmost frame puts half the crash list under one React internal. This walks
 * down to the first frame that is not obviously somebody else's.
 */
function ownFrame(stack: string | null): string {
  if (!stack) return '';
  const lines = stack.split('\n').slice(1);
  const theirs = /node_modules|react-native\/Libraries|\breact-dom\b|\[native code\]|internal\/process/;
  const own = lines.find((l) => l.trim() && !theirs.test(l)) ?? lines[0] ?? '';
  // Drop column and line numbers: the same bug moves between builds, and a
  // fingerprint that changes on every release cannot answer "is this new?".
  return stripVolatile(own.trim()).slice(0, 200);
}

/**
 * A stable grouping key.
 *
 * Deliberately NOT a hash. A readable fingerprint means the report list can be
 * scanned without a lookup table, and an operator can tell at a glance that
 * two lines are the same bug — which is the entire job of this field.
 *
 * The surface is part of the key. The same message from the web app and the
 * native app is usually not the same bug, and when it is, the two lines
 * sitting next to each other says so more clearly than one merged line would.
 */
export function fingerprint(
  error: NormalizedError,
  context: Pick<ReportContext, 'surface'>,
  kind: ReportKind,
): string {
  const shape = stripVolatile(error.message).trim().slice(0, 160);
  const frame = ownFrame(error.stack);
  return [context.surface, kind, error.name, shape, frame].filter(Boolean).join(' | ');
}

/** How many breadcrumbs a report carries. The most recent are kept. */
export const MAX_BREADCRUMBS = 25;

export interface BuildOptions {
  readonly kind: ReportKind;
  readonly context: ReportContext;
  readonly breadcrumbs?: readonly Breadcrumb[];
  /** Injectable so a test is not at the mercy of the clock. */
  readonly now?: Date;
}

/**
 * Assemble a report. This is the only way one should be made.
 *
 * Every string that reaches the wire has been through `scrub`, including
 * breadcrumbs — which are the likeliest carrier of a credential, because a
 * request breadcrumb is a URL and a URL is where API keys live.
 */
export function buildReport(thrown: unknown, opts: BuildOptions): ErrorReport {
  const error = normalizeError(thrown);
  const crumbs = (opts.breadcrumbs ?? []).slice(-MAX_BREADCRUMBS).map((c) => ({
    at: c.at,
    category: c.category,
    message: scrub(c.message, 500),
  }));

  return {
    kind: opts.kind,
    error,
    context: opts.context,
    occurredAt: (opts.now ?? new Date()).toISOString(),
    fingerprint: fingerprint(error, opts.context, opts.kind),
    ...(crumbs.length > 0 ? { breadcrumbs: crumbs } : {}),
  };
}
